/**
 * Constants for the typed User Recents system.
 *
 * Every tracked entity type (apps, profile dropdown links, future dashboards,
 * announcements, etc.) funnels through the same DynamoDB table and the same
 * service + hook. Per-type behavior (cap, TTL, ranking weights) is configured
 * here. TypeScript's `Record<RecentEntityType, …>` enforces that adding a new
 * entity type requires supplying config for every knob — the compiler will not
 * let you ship a half-configured type.
 *
 * See `analysis-landing-page-redesign/recents-tech-spec.md` for the full design.
 */

/**
 * Entity types tracked by the server-side recents system.
 *
 * Values are serialised into the DynamoDB sort key as `<TYPE>#<id>` and
 * therefore MUST stay stable forever. Renames require a data migration plan.
 * Additions are cheap.
 *
 * Note: `GLOBAL_SEARCH` is deliberately NOT in this enum — recent search
 * queries are persisted client-side via `localStorage` rather than in this
 * shared DDB table. See `recents-tech-spec.md` §16 for the rationale.
 */
export enum RecentEntityType {
  /** Apps from `AppContext.parsedApplications`. */
  APP = 'APP',
  /** Profile dropdown links from `PROFILE_DROPDOWN_ITEMS`. */
  PROFILE = 'PROFILE'
}

/** Weights for the hybrid recency + frequency score. Both weights SHOULD sum to 1.0 for comparability. */
export interface RankingWeights {
  readonly recency: number;
  readonly frequency: number;
}

export const RecentsConstants = {
  /** Max number of recents shown per type. Bounds the UI and prevents one type from starving another. */
  CAP_BY_TYPE: {
    [RecentEntityType.APP]: 10,
    [RecentEntityType.PROFILE]: 5
  } as const satisfies Record<RecentEntityType, number>,

  /** TTL for each entity type, in days. Feeds into the DynamoDB `ttl` attribute at write time. */
  TTL_DAYS_BY_TYPE: {
    [RecentEntityType.APP]: 90,
    [RecentEntityType.PROFILE]: 60
  } as const satisfies Record<RecentEntityType, number>,

  /**
   * Per-type ranking weights. Apps benefit from frequency ("daily drivers" beat one-off clicks);
   * profile links do not — "Phone Tool" is hit constantly and frequency would drown out the
   * actually-interesting signal of "what did you just click."
   */
  WEIGHTS_BY_TYPE: {
    [RecentEntityType.APP]: { recency: 0.7, frequency: 0.3 },
    [RecentEntityType.PROFILE]: { recency: 1.0, frequency: 0.0 }
  } as const satisfies Record<RecentEntityType, RankingWeights>,

  // --- Hybrid-score parameters shared across types ---

  /** Half-life for the exponential recency decay, in days. */
  RECENCY_HALF_LIFE_DAYS: 14,

  /**
   * Normalization anchor for the frequency score — `log1p(visitCount) / log1p(ANCHOR)`.
   * A user with ANCHOR visits gets a frequency score of 1.0.
   */
  FREQUENCY_NORMALIZATION_ANCHOR: 100,

  // --- Type-specific knobs ---

  /** APP-only: track visits to external-link apps (opened via `window.open`). */
  APP_TRACK_EXTERNAL: true,

  // --- Client-side recent searches (localStorage, NOT DDB) ---

  /** Config for client-side recent searches. See tech spec §16. */
  GLOBAL_SEARCH: {
    STORAGE_KEY: 'finsuite.recent_searches',
    STORAGE_VERSION: 1,
    CAP: 8,
    MAX_AGE_DAYS: 30,
    MIN_QUERY_LENGTH: 2,
    MAX_QUERY_LENGTH: 200
  }
} as const;
